import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { RichTextEditor } from '../../RichTextEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  order: number;
}

interface FaqData {
  title?: string;
  subtitle?: string;
  layout?: string;
  iconPosition?: string;
  allowMultipleOpen?: boolean;
  defaultOpenFirst?: boolean;
  showNumbers?: boolean;
  items?: FaqItem[];
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
    questionText?: string;
    answerText?: string;
    itemBackground?: string;
    itemBorder?: string;
    iconColor?: string;
    buttonBg?: string;
    buttonText?: string;
  };
  buttons?: ButtonData[];
}

interface FaqEditorProps {
  formData: FaqData;
  setFormData: (data: FaqData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const FaqEditor: React.FC<FaqEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const items = data.items || [];
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const addFaqItem = () => {
    const newItem: FaqItem = {
      id: uuidv4(),
      question: '',
      answer: '',
      order: items.length,
    };
    setFormData({ ...data, items: [...items, newItem] });
  };

  const updateFaqItem = (index: number, field: string, value: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...data, items: updatedItems });
  };

  const removeFaqItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const moveFaqItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('faq', themeColors);

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Frequently Asked Questions"
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Subtitle</label>
        <RichTextEditor
          content={data.subtitle || ''}
          onChange={value => setFormData({ ...data, subtitle: value })}
          placeholder="Add a description for your FAQ section..."
        />
      </div>

      {/* Layout Options */}
      <LayoutOptions data={data} setFormData={setFormData} />

      {/* Toggle Options */}
      <ToggleOptions data={data} setFormData={setFormData} />

      {/* FAQ Items */}
      <FaqItemsSection
        items={items}
        onAddItem={addFaqItem}
        onUpdateItem={updateFaqItem}
        onRemoveItem={removeFaqItem}
        onMoveItem={moveFaqItem}
      />

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Color Customization"
        description="Customize FAQ section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface LayoutOptionsProps {
  data: FaqData;
  setFormData: (data: FaqData) => void;
}

const LayoutOptions: React.FC<LayoutOptionsProps> = ({ data, setFormData }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Layout Style</label>
      <select
        value={data.layout || 'accordion'}
        onChange={e => setFormData({ ...data, layout: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="accordion">Accordion (Expandable)</option>
        <option value="list">List (All Visible)</option>
        <option value="grid">Grid (2 Columns)</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Icon Position</label>
      <select
        value={data.iconPosition || 'right'}
        onChange={e => setFormData({ ...data, iconPosition: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="right">Right</option>
        <option value="left">Left</option>
        <option value="none">None</option>
      </select>
    </div>
  </div>
);

interface ToggleOptionsProps {
  data: FaqData;
  setFormData: (data: FaqData) => void;
}

const ToggleOptions: React.FC<ToggleOptionsProps> = ({ data, setFormData }) => (
  <div className="grid grid-cols-3 gap-4">
    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={data.allowMultipleOpen || false}
        onChange={e => setFormData({ ...data, allowMultipleOpen: e.target.checked })}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="text-sm text-gray-700">Allow Multiple Open</span>
    </label>

    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={data.defaultOpenFirst !== false}
        onChange={e => setFormData({ ...data, defaultOpenFirst: e.target.checked })}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="text-sm text-gray-700">Open First by Default</span>
    </label>

    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={data.showNumbers || false}
        onChange={e => setFormData({ ...data, showNumbers: e.target.checked })}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="text-sm text-gray-700">Show Numbers</span>
    </label>
  </div>
);

interface FaqItemsSectionProps {
  items: FaqItem[];
  onAddItem: () => void;
  onUpdateItem: (index: number, field: string, value: string) => void;
  onRemoveItem: (index: number) => void;
  onMoveItem: (index: number, direction: 'up' | 'down') => void;
}

const FaqItemsSection: React.FC<FaqItemsSectionProps> = ({
  items,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onMoveItem,
}) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <label className="block text-sm font-medium text-gray-700">FAQ Items ({items.length})</label>
      <button
        type="button"
        onClick={onAddItem}
        className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
      >
        <Plus size={16} />
        <span>Add Question</span>
      </button>
    </div>

    <div className="space-y-4">
      {items.map((item, index) => (
        <FaqItemCard
          key={item.id}
          item={item}
          index={index}
          totalItems={items.length}
          onUpdate={(field, value) => onUpdateItem(index, field, value)}
          onRemove={() => onRemoveItem(index)}
          onMove={direction => onMoveItem(index, direction)}
        />
      ))}

      {items.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="mb-2">No FAQ items yet</p>
          <button
            type="button"
            onClick={onAddItem}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Add your first question
          </button>
        </div>
      )}
    </div>
  </div>
);

interface FaqItemCardProps {
  item: FaqItem;
  index: number;
  totalItems: number;
  onUpdate: (field: string, value: string) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
}

const FaqItemCard: React.FC<FaqItemCardProps> = ({
  item,
  index,
  totalItems,
  onUpdate,
  onRemove,
  onMove,
}) => (
  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
    <div className="flex items-start justify-between mb-3">
      <span className="text-sm font-medium text-gray-500">Question {index + 1}</span>
      <div className="flex items-center space-x-1">
        <button
          type="button"
          onClick={() => onMove('up')}
          disabled={index === 0}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button"
          onClick={() => onMove('down')}
          disabled={index === totalItems - 1}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronDown size={16} />
        </button>
        <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600">
          <Trash2 size={16} />
        </button>
      </div>
    </div>

    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Question</label>
        <input
          type="text"
          value={item.question || ''}
          onChange={e => onUpdate('question', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter the question..."
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Answer</label>
        <RichTextEditor
          content={item.answer || ''}
          onChange={value => onUpdate('answer', value)}
          placeholder="Enter the answer..."
        />
      </div>
    </div>
  </div>
);
